import { apiCountryCode, encodeGeo, geoRegionCode } from './geoSelection';

export type CountryStateOption = {
  value: string;
  label: string;
};

export type CountryStateGroup = {
  value: string;
  label: string;
  children?: readonly CountryStateOption[];
};

const AU_STATES: readonly CountryStateOption[] = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'SA', label: 'South Australia' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'ACT', label: 'Australian Capital Territory' },
];

const US_STATES: readonly CountryStateOption[] = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

const STATES_BY_COUNTRY: Record<string, readonly CountryStateOption[]> = {
  AU: AU_STATES,
  US: US_STATES,
};

export const ALL_STATES_LABEL = 'All States';

/** `Victoria (VIC)`; when the name is already the code, keep a single token. */
export function namedCodeLabel(name: string, code: string): string {
  const trimmed = name.trim();
  if (!trimmed || trimmed.toUpperCase() === code) return code;
  return `${trimmed} (${code})`;
}

function intlDisplayName(type: 'region' | 'subdivision', code: string): string | undefined {
  try {
    const name = new Intl.DisplayNames(['en'], { type }).of(code);
    if (!name || name === code) return undefined;
    return name;
  } catch {
    return undefined;
  }
}

/** English country name from the callback, then `Intl`, then the ISO code. */
export function resolvedCountryName(code: string, countryLabel: (code: string) => string): string {
  const fromCallback = countryLabel(code).trim();
  if (fromCallback && fromCallback.toUpperCase() !== code) return fromCallback;
  return intlDisplayName('region', code) ?? fromCallback ?? code;
}

function resolvedStateName(country: string, region: string): string {
  const match = STATES_BY_COUNTRY[country]?.find((state) => state.value === region);
  if (match?.label) return match.label;
  return intlDisplayName('subdivision', `${country}-${region}`) ?? region;
}

function stateLabel(country: string, region: string): string {
  return namedCodeLabel(resolvedStateName(country, region), region);
}

function regionsForCountry(availableCodes: readonly string[], country: string): string[] {
  const prefix = `${country}:`;
  return [
    ...new Set(
      availableCodes
        .map((code) => code.trim().toUpperCase())
        .filter((code) => code.startsWith(prefix))
        .map((code) => geoRegionCode(code))
        .filter((region) => region.length > 0),
    ),
  ].sort((left, right) => left.localeCompare(right));
}

function stateChildren(country: string, availableCodes: readonly string[]): CountryStateOption[] {
  const regions = regionsForCountry(availableCodes, country);
  if (regions.length === 0) return [];
  return [
    { value: country, label: ALL_STATES_LABEL },
    ...regions.map((region) => ({
      value: encodeGeo(country, region),
      label: stateLabel(country, region),
    })),
  ];
}

/** Countries that appear as ISO codes or as the country half of `AU:VIC` keys. */
function availableCountries(availableCodes: readonly string[]): string[] {
  return [
    ...new Set(
      availableCodes
        .map((code) => apiCountryCode(code.trim().toUpperCase()))
        .filter((code) => code && code !== 'UNASSIGNED'),
    ),
  ];
}

/** Groups for Dashboard and Engagement. Options come only from `availableCodes`. */
export function countryStateGroups(
  availableCodes: readonly string[],
  countryLabel: (code: string) => string,
  allCountries: { value: string; label: string },
): CountryStateGroup[] {
  const countryGroups = availableCountries(availableCodes)
    .map((code) => {
      const children = stateChildren(code, availableCodes);
      return {
        value: code,
        label: namedCodeLabel(resolvedCountryName(code, countryLabel), code),
        ...(children.length > 0 ? { children } : {}),
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label));

  return [{ value: allCountries.value, label: allCountries.label }, ...countryGroups];
}

export function countryStateClosedLabel(
  selection: string,
  groups: readonly CountryStateGroup[],
  allCountriesLabel: string,
): string {
  if (!selection) return allCountriesLabel;
  const group = groups.find((item) => {
    if (item.value === selection) return true;
    return item.children?.some((child) => child.value === selection) ?? false;
  });
  if (!group) return allCountriesLabel;
  const region = geoRegionCode(selection);
  if (!region) {
    if (!group.children?.length) return group.label;
    return `${group.label} — ${ALL_STATES_LABEL}`;
  }
  const child = group.children?.find((item) => item.value === selection);
  return `${group.label} — ${child?.label ?? region}`;
}
